<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useAuth } from '../auth.js';
import { useSync } from '../sync.js';
import { useSetups } from '../setups.js';
import { useToasts } from '../use_toasts.js';

const { isLoggedIn, email, logout, scannerAccess, refreshProfile } = useAuth();
const { syncAll, resetSyncState, hasLocalChanges } = useSync();
const { replaceAllSetups } = useSetups();
const { pushToast } = useToasts();

// Two separate trigger/popover pairs — one for desktop (.account-widget,
// where the setup selector/undo-redo already render inline elsewhere in
// the bar, so its popover is just the account-specific items) and one
// for phones (.mobile-widget, where everything collapses into this one
// dropdown instead — see the template comment above it). Only one pair
// is ever visually present at a time (CSS hides the other per
// breakpoint), but both share the same `expanded` state and toggle(), so
// opening/closing whichever is visible just works without the component
// needing to know which breakpoint it's currently in.
const root = ref(null);
const mobileRoot = ref(null);
const expanded = ref(false);
const syncing = ref(false);
const loggingOut = ref(false);

function close() {
  expanded.value = false;
}

function toggle() {
  if (expanded.value) close();
  else expanded.value = true;
}

// `silent` (passed straight through to syncAll, see sync.js's file header)
// suppresses ALL feedback, success or failure — background triggers (page
// load, regained connectivity) shouldn't say anything either way. Only the
// "Sync now" button itself calls this non-silent, so only a deliberate
// click ever reports its own outcome — a truthful one, since syncAll()
// never throws (it catches and reports its own failures internally) and
// its return value, not a try/catch here, is what tells success from
// failure; without checking it this used to claim "Sync complete." even
// when the sync had just failed.
async function onSync({ silent = false } = {}) {
  syncing.value = true;
  try {
    const ok = await syncAll({ silent });
    if (!silent && ok) pushToast('Sync complete.', { variant: 'success' });
  } finally {
    syncing.value = false;
  }
}

async function onLogout() {
  loggingOut.value = true;
  try {
    // Best-effort: give any not-yet-pushed edit one last chance to reach
    // the server before resetSyncState()/replaceAllSetups() below wipe the
    // local bookkeeping (knownServerState, pendingDeletes) that syncAll
    // needs to even know what to push. Bare call, so silent by default
    // (see syncAll) — this is an automatic side effect of clicking Log
    // Out, not itself something to report; it never throws either way, so
    // logout proceeds regardless of whether it worked. The edit itself
    // isn't lost if it fails, only unsynced until logging back in.
    await syncAll();
  } finally {
    loggingOut.value = false;
  }
  logout();
  resetSyncState();
  // Same reasoning as loadFreshAfterLogin (see sync.js): local setups
  // belong to the account that was just logged out of, so they shouldn't
  // linger around for whoever (or whatever account) uses this browser
  // next. Falls back to one bare "Default" setup, same as a fresh install.
  replaceAllSetups([], null);
  close();
}

// Closing on an outside click is the behavior people expect from this kind
// of corner dropdown — without it, the only way to dismiss is logging out
// or syncing, which is a trap for anyone who opened it by mistake. Uses
// composedPath() (the propagation path frozen at dispatch time) rather than
// e.target: a click on a button that itself triggers a reactive re-render
// can get its own element replaced in the DOM before this listener runs
// (browsers run a microtask checkpoint between each individual listener
// invocation during dispatch, and Vue's re-render is scheduled as a
// microtask), which would make a plain root.value.contains(e.target) check
// see a now-detached node and read false even though the click was
// genuinely inside root. composedPath() isn't affected since it's computed
// once, before any listener runs. Checks both root refs since only one is
// ever visible per breakpoint, but both share `expanded`.
function onDocumentClick(e) {
  if (!expanded.value) return;
  const path = e.composedPath();
  const insideDesktop = root.value && path.includes(root.value);
  const insideMobile = mobileRoot.value && path.includes(mobileRoot.value);
  if (!insideDesktop && !insideMobile) close();
}

// The DB is the source of truth once logged in — the app should reconcile
// with it as soon as it reasonably can, not only when someone remembers to
// press "Sync now": once on mount (covers "reload the page" as the
// simplest way to pick up whatever changed elsewhere) and again any time
// the browser regains connectivity after being offline.
function onOnline() {
  if (isLoggedIn.value) onSync({ silent: true });
}

onMounted(() => {
  document.addEventListener('click', onDocumentClick);
  window.addEventListener('online', onOnline);
  if (isLoggedIn.value) onSync({ silent: true });
  // Refreshes scannerAccess for the Score Sheet Scanner link below — lives
  // here (rather than only on the pages that show it) since this widget is
  // shared across every page, and scanner access can be granted server-side
  // (a direct DB edit) after this browser already logged in.
  refreshProfile();
});
onUnmounted(() => {
  document.removeEventListener('click', onDocumentClick);
  window.removeEventListener('online', onOnline);
});
</script>

<template>
  <div class="account-bar">
    <!-- The app name doubles as the home link — every page shows the
         same one, so it lives here rather than as a prop threaded
         through app.vue/profile/app.vue/score-sheet-scanner/app.vue;
         each of those already has its own page heading (see their
         CollapsiblePanel's own title) and no longer needs a second,
         separate one above the layout. -->
    <a href="/" class="app-title">Sight Marks Calculator</a>

    <!-- Desktop only (hidden on phones — see the media query below):
         page-specific actions (e.g. app.vue's undo/redo), centered as
         the thing most worth a glance after the active setup itself.
         Each page passes its own via this default slot rather than
         account_widget.vue knowing about every page that exists. -->
    <div class="account-bar-center">
      <slot />
    </div>

    <!-- Desktop only: setup selector grouped with the account trigger at
         the bar's far right. -->
    <div class="account-bar-right">
      <slot name="right" />

      <div class="account-widget" ref="root">
        <a v-if="!isLoggedIn" href="/login/" class="btn-copy account-trigger">Log In</a>

        <template v-else>
          <button type="button" class="btn-copy account-trigger" @click="toggle">
            Profile <span class="caret">▾</span>
          </button>

          <div v-if="expanded" class="account-popover">
            <p class="account-email">{{ email }}</p>
            <button v-if="hasLocalChanges" type="button" class="btn-copy" @click="onSync()" :disabled="syncing">
              {{ syncing ? 'Syncing…' : 'Sync now' }}
            </button>
            <a class="btn-copy account-profile-link" href="/profile/">Profile</a>
            <a class="btn-copy account-profile-link" href="/setups/">Setups</a>
            <a v-if="scannerAccess" class="btn-copy account-profile-link" href="/score-sheet-scanner/">Score Sheet Scanner</a>
            <button type="button" class="btn-delete" @click="onLogout" :disabled="loggingOut">
              {{ loggingOut ? 'Logging out…' : 'Log Out' }}
            </button>
          </div>
        </template>
      </div>
    </div>

    <!-- Phone only (hidden on desktop): everything the two zones above
         hold — undo/redo, the setup selector, and the account menu —
         collapses into this ONE dropdown instead, so opening it never
         reveals a second nested trigger to tap through (a Profile button
         that then needs its own separate tap to reveal Profile/Log Out/
         etc). Always shows a toggle regardless of login state, unlike
         the desktop trigger above, since undo/redo need to stay reachable
         even logged out — only the account-specific section at the
         bottom is login-gated. -->
    <div class="mobile-widget" ref="mobileRoot">
      <button
        type="button"
        class="mobile-toggle"
        @click="toggle"
        :aria-expanded="expanded"
        aria-label="Menu"
      >☰</button>

      <div v-if="expanded" class="mobile-popover">
        <div class="mobile-popover-actions">
          <slot />
        </div>

        <div class="mobile-popover-setup">
          <slot name="right" />
        </div>

        <div class="mobile-popover-account">
          <template v-if="isLoggedIn">
            <p class="account-email">{{ email }}</p>
            <button v-if="hasLocalChanges" type="button" class="btn-copy" @click="onSync()" :disabled="syncing">
              {{ syncing ? 'Syncing…' : 'Sync now' }}
            </button>
            <a class="btn-copy account-profile-link" href="/profile/">Profile</a>
            <a class="btn-copy account-profile-link" href="/setups/">Setups</a>
            <a v-if="scannerAccess" class="btn-copy account-profile-link" href="/score-sheet-scanner/">Score Sheet Scanner</a>
            <button type="button" class="btn-delete" @click="onLogout" :disabled="loggingOut">
              {{ loggingOut ? 'Logging out…' : 'Log Out' }}
            </button>
          </template>
          <a v-else href="/login/" class="btn-copy account-profile-link">Log In</a>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>

/* A normal, static, in-flow header bar — not fixed, not sticky. It sits at
   the top of the page once and then scrolls away with everything else,
   rather than floating on top of content (a fixed corner overlay) or
   re-pinning itself over content while scrolling (position: sticky).

   Three columns (1fr auto right) rather than a two-cluster flex row —
   the center column (undo/redo etc.) needs to land at the bar's true
   visual center regardless of how wide the title and right-hand group
   are, which justify-content: space-between can't do (it only centers
   relative to the space between its neighbors, not the whole bar).
   margin-bottom gives the bar its own breathing room below the border,
   rather than page content starting flush against it. */
.account-bar {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-sm) var(--space-md);
  background: var(--color-bg);
  border-bottom: var(--border-width) solid var(--color-border);
  margin-bottom: var(--space-md);
  box-sizing: border-box;
}

.account-bar-center {
  justify-self: center;
  display: flex;
  align-items: center;
}

.account-bar-right {
  justify-self: end;
  display: flex;
  align-items: center;
  gap: var(--space-sm);
}

/* The site wordmark — plain text-like styling, no button chrome, so it
   reads as the page's title (replacing the old separate centered <h3>
   below the bar) rather than another action alongside the account
   trigger/setup selector. */
.app-title {
  justify-self: start;
  font-size: 1.25rem;
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
  text-decoration: none;
  white-space: nowrap;
}

.app-title:hover {
  opacity: 0.85;
}

/* Hidden above the phone breakpoint — an element with display: none is
   excluded from grid placement entirely, so .account-bar's three
   remaining children (title/center/right) still land cleanly one per
   column without this needing an explicit grid-column of its own. */
.mobile-widget {
  display: none;
}

.account-widget,
.mobile-widget {
  position: relative; /* containing block for the popover below */
}

/* Overridden to a neutral outline rather than .btn-copy's solid accent
   fill — that color is reserved for the active-setup selector (see
   setup_select.vue), the one thing in the top bar actually worth a
   glance; the account trigger is identity chrome, not the day-to-day
   focal point. */
.account-trigger {
  background: var(--color-bg);
  color: var(--color-text);
  border: var(--border-width) solid var(--color-border);
  white-space: nowrap;
  /* The logged-out trigger is an <a>, not a <button> — .btn-copy styles
     both, but an <a> still needs its own display/decoration reset (same
     reasoning as .account-profile-link below). */
  display: inline-block;
  text-decoration: none;
}

.account-trigger:hover {
  background: var(--color-bg-alt);
  opacity: 1; /* overrides .btn-copy:hover's opacity dim, which reads oddly on a bordered/light fill */
}

.caret {
  display: inline-block;
  opacity: 0.8;
}

/* The phone menu's trigger — a plain hamburger icon rather than a
   labeled button, since it stands for everything in the bar (undo/redo,
   setup, account) rather than any one of those things specifically, the
   way "Profile" or "Menu" text would imply. */
.mobile-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25em;
  height: 2.25em;
  padding: 0;
  background: var(--color-bg);
  color: var(--color-text);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--border-radius);
  font-size: 1.1rem;
  line-height: 1;
  cursor: pointer;
}

.mobile-toggle:hover {
  background: var(--color-bg-alt);
}

.account-email {
  margin: 0;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  text-align: center;
  word-break: break-word;
}

.account-popover {
  position: absolute;
  top: calc(100% + var(--space-xs));
  /* Opens leftward from the trigger's right edge, not rightward from its
     left edge — the trigger now sits flush against the bar's right edge
     (see .account-bar-right), so a popover expanding further right would
     run past the viewport instead of staying on-screen. */
  right: 0;
  /* Without this, later-in-DOM page content (the panels in .layout) paints
     over this absolutely-positioned popover whenever both sit at the same
     (auto) stacking level — later DOM order wins ties, regardless of who's
     visually on top. Below the toast stack's z-index (1000, see
     error_popups.vue and its per-page duplicates) so a toast is never
     hidden behind this dropdown. */
  z-index: 900;
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
  background: var(--color-bg);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--border-radius);
  box-shadow: var(--shadow-md);
  padding: var(--space-md);
  min-width: 220px;
  box-sizing: border-box;
}

/* Every action in the popover — Sync now/Profile/Score Sheet
   Scanner/Log Out — at the same size, overriding whatever padding/
   font-size/border-radius its own class (.btn-copy vs .btn-delete, which
   are otherwise sized differently for their usual context: a full menu
   item here vs. a small inline delete elsewhere in the app, e.g. removing
   a mark row) would give it on its own. */
.account-popover button,
.account-popover a {
  width: 100%;
  box-sizing: border-box;
  padding: var(--space-sm) var(--space-md);
  border-radius: var(--border-radius);
  font-size: var(--font-size-base);
  text-align: center;
}

/* .btn-copy is styled for <button> elements (no `display` set, so it falls
   back to a button's own UA default) — this is an <a> instead (a real page
   navigation to /profile/, not an in-popover action), which defaults to
   inline and picks up an underline, so both need overriding here. Flex
   items are blockified regardless of their own `display` per spec, so this
   still lines up with its sibling buttons in the popover's column. */
.account-profile-link {
  display: block;
  text-decoration: none;
}

/* Phone layout: the desktop-only center/right zones (undo/redo, setup
   selector, account trigger — see .account-bar-center/.account-bar-right
   above) disappear entirely rather than wrapping into a cramped
   multi-row mess; .mobile-widget's own single trigger replaces them,
   landing in the same column 2 spot the grid already reserves. */
@media (max-width: 700px) {
  .account-bar {
    grid-template-columns: 1fr auto;
  }

  .account-bar-center,
  .account-bar-right {
    display: none;
  }

  .mobile-widget {
    display: block;
  }
}

.mobile-popover {
  position: absolute;
  top: calc(100% + var(--space-xs));
  /* Opens leftward from the trigger's right edge — same reasoning as
     .account-popover above, since .mobile-widget sits in the bar's
     rightmost column too. */
  right: 0;
  z-index: 900;
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
  background: var(--color-bg);
  border: var(--border-width) solid var(--color-border);
  border-radius: var(--border-radius);
  box-shadow: var(--shadow-md);
  padding: var(--space-md);
  width: min(300px, calc(100vw - 2 * var(--space-md)));
  box-sizing: border-box;
}

.mobile-popover-actions {
  display: flex;
  justify-content: center;
  gap: var(--space-xs);
}

.mobile-popover-setup {
  width: 100%;
}

/* Separated from the undo/redo & setup selector above by a divider —
   those two are ordinary page actions, this section is account-specific
   (email/sync/profile/log out), grouped the same way the desktop
   .account-popover keeps it distinct from the rest of the bar. */
.mobile-popover-account {
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
  padding-top: var(--space-sm);
  border-top: var(--border-width) solid var(--color-border);
}

/* Same sizing normalization as .account-popover's version above (see its
   comment) — a separate rule since .mobile-popover-actions/-setup hold
   other components' own content (undo/redo, the setup select) that
   shouldn't be stretched/resized the same way. */
.mobile-popover-account button,
.mobile-popover-account a {
  width: 100%;
  box-sizing: border-box;
  padding: var(--space-sm) var(--space-md);
  border-radius: var(--border-radius);
  font-size: var(--font-size-base);
  text-align: center;
}

</style>
